// Tenant list data for the operator console (mockup 41-admin-tenants-list).
//
// Wired (seam-wiring D-L1 / F-L1): listTenants()/getTenant() -> GET /admin/tenants
// + /admin/tenants/:id (operator/AM-scoped, cross-tenant), and createTenant() ->
// POST /admin/tenants (the new-tenant wizard mutation). Vertical is mapped
// server-side; plan/status come from tenant.config until the subscriptions domain
// exists; responseTimeS reads 0 until SLA is measured.

import { isAxiosError } from 'axios'

import { apiClient } from '../../api/client.js'

export type TenantVertical = 'salud' | 'autos_nuevos' | 'autos_seminuevos' | 'empeno' | 'medico'
export type TenantPlan = 'basico' | 'profesional' | 'empresarial'
export type TenantStatus = 'active' | 'past_due' | 'courtesy' | 'onboarding' | 'sin_suscripcion'

export interface Tenant {
  id: string
  name: string
  vertical: TenantVertical
  plan: TenantPlan
  status: TenantStatus
  /** Lifecycle suspend (active=false) — distinct from the billing status above. */
  suspended?: boolean
  /** Still in onboarding (not yet taken live) — drives the go-live button. */
  onboarding?: boolean
  /** The invited owner has not activated their account yet (pending owner invite,
   *  no owner membership). Drives the "Invitación pendiente" badge/banner. */
  ownerInvitePending?: boolean
  accountManager: { name: string }
  conversations30d: number
}

export const VERTICAL_LABEL: Record<TenantVertical, string> = {
  salud: 'Salud',
  autos_nuevos: 'Autos nuevos',
  autos_seminuevos: 'Autos seminuevos',
  empeno: 'Casa de empeño',
  medico: 'Médico',
}

export const PLAN_LABEL: Record<TenantPlan, string> = {
  basico: 'Básico',
  profesional: 'Profesional',
  empresarial: 'Empresarial',
}

export const STATUS_BADGE: Record<TenantStatus, { tone: 'ok' | 'warn' | 'info'; label: string }> = {
  active: { tone: 'ok', label: 'Activo' },
  past_due: { tone: 'warn', label: 'Pendiente de pago' },
  courtesy: { tone: 'info', label: 'Cortesía' },
  onboarding: { tone: 'info', label: 'En alta' },
  sin_suscripcion: { tone: 'warn', label: 'Sin suscripción' },
}

/** GET /admin/tenants — the cross-tenant client list (operator/AM scoped). */
export async function listTenants(): Promise<Tenant[]> {
  const res = await apiClient.get<{ data: Tenant[] }>('/admin/tenants')
  return res.data.data
}

// --- Tenant detail -------------------------------------------------

export type BotStatus = 'active' | 'paused' | 'draft'

export interface TenantBot {
  id: string
  name: string
  intent: string
  status: BotStatus
}

export interface TenantDetail extends Tenant {
  bots: TenantBot[]
  /** Share of conversations resolved without a human, last 30 days (0–100). */
  noHumanRate: number
  /** Median first-response time, seconds. */
  responseTimeS: number
  /** When the client was created (ISO 8601). */
  createdAt: string
  /** The account owner's email (tenant_owner membership), null if none yet. */
  ownerEmail: string | null
  /** The account owner's display name (tenant_owner membership), null if none yet. */
  ownerName?: string | null
  /** While ownerInvitePending, the email the invite was sent to (null once accepted). */
  ownerInviteEmail?: string | null
}

export const BOT_STATUS_LABEL: Record<BotStatus, { tone: 'ok' | 'warn' | 'draft'; label: string }> =
  {
    active: { tone: 'ok', label: 'Activo' },
    paused: { tone: 'warn', label: 'Pausado' },
    draft: { tone: 'draft', label: 'Borrador' },
  }

// --- New tenant (mockup 43 wizard) --------------------------------------------

export interface NewTenantInput {
  name: string
  vertical: TenantVertical
  plan: TenantPlan
  botName: string
  welcomeMessage: string
  owner: { name: string; email: string }
}

/**
 * POST /admin/tenants — atomically creates the tenant (status onboarding), its
 * first bot (draft, seeded from the vertical template) and the owner invite.
 */
export async function createTenant(input: NewTenantInput): Promise<{ id: string }> {
  // Wired: creates the tenant (status onboarding) + its first
  // bot (draft, seeded from the vertical template) + a tenant_owner invite for
  // input.owner, atomically. Returns the new tenant id.
  const res = await apiClient.post<{ data: { id: string } }>('/admin/tenants', input)
  return res.data.data
}

/** GET /admin/tenants/:id — one client's detail; null on 404, rethrows other errors. */
export async function getTenant(id: string): Promise<TenantDetail | null> {
  try {
    const res = await apiClient.get<{ data: TenantDetail }>(`/admin/tenants/${id}`)
    return res.data.data
  } catch (err) {
    if (isAxiosError(err) && err.response?.status === 404) return null
    throw err
  }
}

// ─── Tenant lifecycle management (slice F) ───────────────────────────────────

export interface UpdateTenantInput {
  name?: string
  plan?: TenantPlan
  accountManagerId?: string | null
}

/** PATCH /admin/tenants/:id — edits name, plan or assigned account manager. */
export async function updateTenant(id: string, patch: UpdateTenantInput): Promise<void> {
  await apiClient.patch(`/admin/tenants/${id}`, patch)
}

/** POST /admin/tenants/:id/suspend — lifecycle suspend (active=false), distinct from billing status. */
export async function suspendTenant(id: string): Promise<void> {
  await apiClient.post(`/admin/tenants/${id}/suspend`, {})
}

/** POST /admin/tenants/:id/reactivate — lifts a lifecycle suspension. */
export async function reactivateTenant(id: string): Promise<void> {
  await apiClient.post(`/admin/tenants/${id}/reactivate`, {})
}

/** Resend the owner's activation invite (mints a fresh link + re-emails it). */
export async function resendOwnerInvite(id: string): Promise<void> {
  await apiClient.post(`/admin/tenants/${id}/resend-owner-invite`, {})
}

/** Hard delete (owner-only). `confirmName` must equal the tenant's exact name. */
export async function deleteTenant(id: string, confirmName: string): Promise<void> {
  await apiClient.delete(`/admin/tenants/${id}`, { data: { confirm: confirmName } })
}

/** Take the client to production: activate its bot(s) + flip onboarding→active.
 *  Requires a connected WhatsApp number (else the API returns 400). */
export async function goLiveTenant(id: string): Promise<void> {
  await apiClient.post(`/admin/tenants/${id}/go-live`, {})
}

// ─── Client roster (operator "Equipo" tab, read-only) ────────────────────────

export interface TenantMember {
  id: string
  name: string
  email: string
  role: string
  roleLabel: string
  status: 'active' | 'inactive' | 'pending'
}

/** The client's full user roster (joined members + pending invites), read-only.
 *  Wired to GET /admin/tenants/:id/members. */
/** B10 concierge team ops — "agrega a mi recepcionista" arrives by WhatsApp;
 *  these act on the CLIENT's team from the console. Server enforces the same
 *  rails as the tenant side (owner untouchable, sessions revoked). */
export async function inviteTenantMember(
  tenantId: string,
  input: { email: string; role: 'tenant_admin' | 'tenant_agent' | 'tenant_viewer' },
): Promise<void> {
  await apiClient.post(`/admin/tenants/${encodeURIComponent(tenantId)}/invitations`, input)
}

/**
 * PATCH /admin/tenants/:t/members/:userId — changes a CLIENT member's role from
 * the console. Server enforces the tenant-side rails (owner untouchable,
 * sessions revoked on demotion).
 */
export async function changeTenantMemberRole(
  tenantId: string,
  userId: string,
  role: 'tenant_admin' | 'tenant_agent' | 'tenant_viewer',
): Promise<void> {
  await apiClient.patch(
    `/admin/tenants/${encodeURIComponent(tenantId)}/members/${encodeURIComponent(userId)}`,
    { role },
  )
}

/** DELETE /admin/tenants/:t/members/:userId — removes a client member (owner refused server-side). */
export async function removeTenantMember(tenantId: string, userId: string): Promise<void> {
  await apiClient.delete(
    `/admin/tenants/${encodeURIComponent(tenantId)}/members/${encodeURIComponent(userId)}`,
  )
}

/** GET /admin/tenants/:id/members — the client's full roster, joined members + pending invites. */
export async function getTenantMembers(id: string): Promise<TenantMember[]> {
  const res = await apiClient.get<{ data: { members: TenantMember[] } }>(
    `/admin/tenants/${id}/members`,
  )
  return res.data.data.members
}

// ─── AI Workers (F-L3 multi-worker) ──────────────────────────────────────────

export interface Worker {
  id: string
  name: string
  role: string
  status: BotStatus
  /** Display number of the worker's connected WhatsApp line, if any. */
  number: string | null
  connected: boolean
}

export interface WorkerTemplate {
  id: string
  key: string
  name: string
  role: string
  blurb: string
}

export interface CreateWorkerInput {
  workerTemplateId?: string
  name?: string
  role?: string
}

/** A client's AI Workers (one bot_instance each) + their connected line. */
export async function listWorkers(tenantId: string): Promise<Worker[]> {
  const res = await apiClient.get<{ data: Worker[] }>(`/admin/tenants/${tenantId}/workers`)
  return res.data.data
}

/** The named-persona catalog for the client's vertical (to seed a new worker). */
export async function listWorkerTemplates(tenantId: string): Promise<WorkerTemplate[]> {
  const res = await apiClient.get<{ data: WorkerTemplate[] }>(
    `/admin/tenants/${tenantId}/worker-templates`,
  )
  return res.data.data
}

/** Create a worker — from a persona template or a custom name/role. */
export async function createWorker(tenantId: string, input: CreateWorkerInput): Promise<Worker> {
  const res = await apiClient.post<{ data: Worker }>(`/admin/tenants/${tenantId}/workers`, input)
  return res.data.data
}

/** Soft-delete a worker (its history is kept; its line is freed). */
export async function deleteWorker(tenantId: string, workerId: string): Promise<void> {
  await apiClient.delete(`/admin/tenants/${tenantId}/workers/${workerId}`)
}

/** Disconnect just this worker's WhatsApp number (keep the worker). */
export async function disconnectWorkerNumber(tenantId: string, workerId: string): Promise<void> {
  await apiClient.delete(`/admin/tenants/${tenantId}/workers/${workerId}/whatsapp`)
}

export interface NumberBinding {
  bound: boolean
  active?: boolean
  tenant?: { id: string; name: string }
  bot?: { id: string; name: string }
}

/** Look up which client currently owns a WhatsApp number (operator tool). */
export async function getNumberBinding(phoneNumberId: string): Promise<NumberBinding> {
  const res = await apiClient.get<{ data: NumberBinding }>(
    `/admin/whatsapp/numbers/${encodeURIComponent(phoneNumberId)}`,
  )
  return res.data.data
}

/** Free a number from its current client so it can be reconnected. */
export async function releaseNumber(phoneNumberId: string): Promise<void> {
  await apiClient.post(`/admin/whatsapp/numbers/${encodeURIComponent(phoneNumberId)}/release`, {})
}

/** Pause or resume a worker. Paused: still receives + stores messages (visible
 *  in the inbox) but the bot does not auto-reply. */
export async function setWorkerStatus(
  tenantId: string,
  workerId: string,
  status: 'active' | 'paused',
): Promise<void> {
  await apiClient.patch(`/admin/tenants/${tenantId}/workers/${workerId}`, { status })
}

export interface OrgUser {
  id: string
  name: string
  email: string
  platform_role: string
}

/** INHERTA team members (platform users) — the pool of possible account managers. */
export async function listOrgUsers(): Promise<OrgUser[]> {
  const res = await apiClient.get<{ data: { members: OrgUser[] } }>('/admin/users')
  return res.data.data.members
}

// ─── WhatsApp connection (slice F-L2) ────────────────────────────────────────

export interface WhatsappStatus {
  connected: boolean
  phoneNumber?: string
  phoneNumberId?: string
  wabaId?: string | null
}

/** GET /admin/tenants/:id/whatsapp — whether a number is stored for the tenant (no Meta probe; see checkWhatsappHealth). */
export async function getWhatsappStatus(tenantId: string): Promise<WhatsappStatus> {
  const res = await apiClient.get<{ data: WhatsappStatus }>(`/admin/tenants/${tenantId}/whatsapp`)
  return res.data.data
}

export interface ConnectWhatsappInput {
  phoneNumberId: string
  wabaId: string
  accessToken: string
  displayNumber: string
  /** Bind the number to a specific worker (bot). Omit → the tenant's first bot. */
  botId?: string
}

export interface ConnectWhatsappResult {
  connected: boolean
  /** Whether our app was subscribed to the WABA's webhooks (false → retry; the
   *  number is stored but won't receive messages until subscribed). */
  webhookSubscribed: boolean
}

/**
 * POST /admin/tenants/:id/whatsapp — stores the number's credentials and
 * subscribes our app to the WABA's webhooks. `webhookSubscribed: false` means
 * the number is saved but receives nothing until a retry succeeds.
 */
export async function connectWhatsapp(
  tenantId: string,
  input: ConnectWhatsappInput,
): Promise<ConnectWhatsappResult> {
  const res = await apiClient.post<{ data: ConnectWhatsappResult }>(
    `/admin/tenants/${tenantId}/whatsapp`,
    input,
  )
  return res.data.data
}

/** Disconnect the tenant's WhatsApp number (deactivates the line). */
export async function disconnectWhatsapp(tenantId: string): Promise<void> {
  await apiClient.delete(`/admin/tenants/${tenantId}/whatsapp`)
}

export type WhatsappHealthStatus = 'active' | 'token_invalid' | 'not_connected' | 'unknown'

export interface WhatsappHealth {
  /** Live result of probing Meta with the stored token (not just "a row exists"). */
  status: WhatsappHealthStatus
  verifiedName?: string
  qualityRating?: string
}

/** Ask Meta (via our API) whether the stored token still works + number is live. */
export async function checkWhatsappHealth(tenantId: string): Promise<WhatsappHealth> {
  const res = await apiClient.get<{ data: WhatsappHealth }>(
    `/admin/tenants/${tenantId}/whatsapp/health`,
  )
  return res.data.data
}

export interface WhatsappTestResult {
  sent: boolean
  messageId?: string
  /** Meta's rejection (code + message), surfaced verbatim so the operator can act. */
  error?: { code?: number; message?: string }
}

/** Send a real test WhatsApp message through the tenant's connected credentials. */
export async function sendWhatsappTest(
  tenantId: string,
  recipient: string,
  text?: string,
): Promise<WhatsappTestResult> {
  const res = await apiClient.post<{ data: WhatsappTestResult }>(
    `/admin/tenants/${tenantId}/whatsapp/test`,
    { recipient, ...(text ? { text } : {}) },
  )
  return res.data.data
}

export interface Provider {
  id: string
  name: string
  specialty: string
  location: string | null
  status: 'active' | 'inactive'
}

export interface NewProvider {
  name: string
  specialty: string
  location?: string
}

export interface ProviderPatch {
  name?: string
  specialty?: string
  location?: string | null
  status?: 'active' | 'inactive'
}

/** A client's providers (doctors), as seen by an operator. */
export async function listProviders(tenantId: string): Promise<Provider[]> {
  const res = await apiClient.get<{ data: { providers: Provider[] } }>(
    `/admin/tenants/${tenantId}/providers`,
  )
  return res.data.data.providers
}

export async function createProvider(tenantId: string, input: NewProvider): Promise<Provider> {
  const res = await apiClient.post<{ data: Provider }>(
    `/admin/tenants/${tenantId}/providers`,
    input,
  )
  return res.data.data
}

export async function updateProvider(
  tenantId: string,
  providerId: string,
  patch: ProviderPatch,
): Promise<Provider> {
  const res = await apiClient.patch<{ data: Provider }>(
    `/admin/tenants/${tenantId}/providers/${providerId}`,
    patch,
  )
  return res.data.data
}

// ─── Provider schedules + time-off (operator, cross-tenant) ──────────────────
// Same shapes the owner-facing agenda-data.ts uses, so the schedule editor is
// identical on both sides — only the URL prefix differs.

export interface ProviderScheduleBlock {
  id: string
  weekday: number // 0=Sun..6=Sat
  startTime: string // 'HH:MM'
  endTime: string
  slotMinutes: number
}

export interface ProviderTimeOff {
  id: string
  startsAt: string
  endsAt: string
  reason: string | null
}

export interface ProviderSchedule {
  blocks: ProviderScheduleBlock[]
  timeOff: ProviderTimeOff[]
}

export interface WeeklyBlockInput {
  weekday: number
  startTime: string
  endTime: string
  slotMinutes?: number
}

/** GET /admin/tenants/:t/providers/:p/schedule — weekly blocks + time-off, same shape as the owner agenda. */
export async function getProviderSchedule(
  tenantId: string,
  providerId: string,
): Promise<ProviderSchedule> {
  const res = await apiClient.get<{ data: ProviderSchedule }>(
    `/admin/tenants/${tenantId}/providers/${providerId}/schedule`,
  )
  return res.data.data
}

/** PUT /admin/tenants/:t/providers/:p/schedule — replaces the whole weekly block set. */
export async function saveProviderSchedule(
  tenantId: string,
  providerId: string,
  blocks: WeeklyBlockInput[],
): Promise<void> {
  await apiClient.put(`/admin/tenants/${tenantId}/providers/${providerId}/schedule`, { blocks })
}

/** POST /admin/tenants/:t/providers/:p/time-off — blocks a date range from booking. */
export async function addProviderTimeOff(
  tenantId: string,
  providerId: string,
  input: { startsAt: string; endsAt: string; reason?: string },
): Promise<ProviderTimeOff> {
  const res = await apiClient.post<{ data: ProviderTimeOff }>(
    `/admin/tenants/${tenantId}/providers/${providerId}/time-off`,
    input,
  )
  return res.data.data
}

/** DELETE /admin/tenants/:t/providers/:p/time-off/:id — reopens the blocked range. */
export async function deleteProviderTimeOff(
  tenantId: string,
  providerId: string,
  timeOffId: string,
): Promise<void> {
  await apiClient.delete(`/admin/tenants/${tenantId}/providers/${providerId}/time-off/${timeOffId}`)
}

// ─── Catalog (estudios / servicios / precios), operator cross-tenant ─────────
// Same shapes the owner-facing catalog-data.ts uses. Prices are pesos (priceMxn)
// on the wire; the API stores cents.

export interface CatalogItem {
  id: string
  name: string
  category: string | null
  priceMxn: number
  description: string | null
  active: boolean
}

export interface NewCatalogItem {
  name: string
  category?: string
  priceMxn: number
  description?: string
}

export interface CatalogItemPatch {
  name?: string
  category?: string | null
  priceMxn?: number
  description?: string | null
  active?: boolean
}

/**
 * GET /admin/tenants/:id/catalog — the client's estudios/servicios. `priceMxn`
 * is pesos on the wire; the API stores cents.
 */
export async function listCatalog(tenantId: string): Promise<CatalogItem[]> {
  const res = await apiClient.get<{ data: { items: CatalogItem[] } }>(
    `/admin/tenants/${tenantId}/catalog`,
  )
  return res.data.data.items
}

export async function createCatalogItem(
  tenantId: string,
  input: NewCatalogItem,
): Promise<CatalogItem> {
  const res = await apiClient.post<{ data: CatalogItem }>(
    `/admin/tenants/${tenantId}/catalog`,
    input,
  )
  return res.data.data
}

export async function updateCatalogItem(
  tenantId: string,
  itemId: string,
  patch: CatalogItemPatch,
): Promise<CatalogItem> {
  const res = await apiClient.patch<{ data: CatalogItem }>(
    `/admin/tenants/${tenantId}/catalog/${itemId}`,
    patch,
  )
  return res.data.data
}

// ─── Business profile (fiscal / contact / brand), operator cross-tenant ──────
// The legal name + RFC an operator registers for a client (what invoicing needs)
// plus contact email, timezone, and brand. Lands on tenants.config; the owner
// Settings page reads it back (legal name + RFC shown locked there).

/** One of a client's conversations, as the operator list shows it. */
export interface AdminConversationRow {
  id: string
  state: string
  channel: string
  contact: string
  lastActivityAt: string
}

/** GET /admin/tenants/:id/conversations — the client's conversation list for the operator detail. */
export async function listTenantConversations(tenantId: string): Promise<AdminConversationRow[]> {
  const res = await apiClient.get<{ data: AdminConversationRow[] }>(
    `/admin/tenants/${encodeURIComponent(tenantId)}/conversations`,
  )
  return res.data.data
}

// ── A client's subscription, as the operator sees it (Phase 3a.8) ──
/** Mirror of GET /admin/tenants/:id/go-live-readiness — the same assessor the
 *  Publicar gate runs, so the checklist can never promise what the gate would
 *  refuse. profile.* are advisory; the other four are the hard gates. */
export interface GoLiveReadiness {
  profile: { billingEmail: boolean; fiscal: boolean }
  channel: boolean
  bot: boolean
  subscription: boolean
  paid: boolean
  ready: boolean
}

/**
 * GET /admin/tenants/:id/go-live-readiness — runs the same assessor as the
 * Publicar gate, so the checklist never promises what go-live would refuse.
 */
export async function getGoLiveReadiness(tenantId: string): Promise<GoLiveReadiness> {
  const res = await apiClient.get<{ data: GoLiveReadiness }>(
    `/admin/tenants/${encodeURIComponent(tenantId)}/go-live-readiness`,
  )
  return res.data.data
}

export interface AdminSubscription {
  id: string
  tierKey: string
  status: string
  /** Centavos. */
  priceMxn: number | null
  customPriceMxn: number | null
  currency: string
  contractStart: string
  contractEndMin: string
  billingCycle: string
  stripeCustomerId: string | null
  stripeSubscriptionId: string | null
  compReason: string | null
  createdAt: string
}

/** GET /admin/tenants/:id/subscription — the client's subscription (prices in centavos), or null when none exists. */
export async function getTenantSubscription(tenantId: string): Promise<AdminSubscription | null> {
  const res = await apiClient.get<{ data: AdminSubscription | null }>(
    `/admin/tenants/${encodeURIComponent(tenantId)}/subscription`,
  )
  return res.data.data
}

/** Mirrors CreateBody on POST /admin/tenants/:id/subscription. The negotiated
 *  price leads: the one seeded tier ('standard') has NO base price by design —
 *  every real deal prices bespoke, so `customPriceMxn` is what the salesperson
 *  closed at. Centavos. */
export interface CreateSubscriptionInput {
  tierKey: string
  status: 'pending_payment_setup' | 'comped'
  /** ISO date (yyyy-mm-dd). */
  contractStart: string
  /** ISO date — the minimum commitment the client signed. */
  contractEndMin: string
  billingCycle?: 'monthly' | 'quarterly' | 'annual'
  customPriceMxn?: number | null
  discountPct?: number | null
  compReason?: string
  compUntil?: string | null
}

/**
 * POST /admin/tenants/:id/subscription — records the closed deal. The
 * negotiated `customPriceMxn` (centavos) leads; the seeded tier has no base
 * price by design.
 */
export async function createTenantSubscription(
  tenantId: string,
  input: CreateSubscriptionInput,
): Promise<AdminSubscription> {
  const res = await apiClient.post<{ data: AdminSubscription }>(
    `/admin/tenants/${encodeURIComponent(tenantId)}/subscription`,
    input,
  )
  return res.data.data
}

export interface SubscriptionPatch {
  tierKey?: string
  customPriceMxn?: number | null
  status?: 'active' | 'comped' | 'pending_payment_setup'
  compReason?: string
  prorate?: boolean
}

/**
 * PATCH /admin/tenants/:id/subscription — tier/price/status changes on the
 * existing subscription; `prorate` controls Stripe's mid-cycle behaviour.
 */
export async function patchTenantSubscription(
  tenantId: string,
  patch: SubscriptionPatch,
): Promise<AdminSubscription> {
  const res = await apiClient.patch<{ data: AdminSubscription }>(
    `/admin/tenants/${encodeURIComponent(tenantId)}/subscription`,
    patch,
  )
  return res.data.data
}

/** Cancel a client's subscription. At period end by default. */
export async function cancelTenantSubscription(
  tenantId: string,
  timing: 'at_period_end' | 'immediate',
  reason?: string,
): Promise<void> {
  await apiClient.delete(`/admin/tenants/${encodeURIComponent(tenantId)}/subscription`, {
    data: { timing, ...(reason ? { reason } : {}) },
  })
}

export interface BusinessProfile {
  displayName: string
  legalName: string | null
  rfc: string | null
  /** Fiscal address. Unlocks SPEI on Stripe and is required for CFDI 4.0. */
  fiscalPostalCode: string | null
  fiscalCity: string | null
  fiscalState: string | null
  fiscalAddress: string | null
  /** SAT c_RegimenFiscal and c_UsoCFDI. Both MANDATORY on a CFDI 4.0 receptor,
   *  and neither existed anywhere before — the concrete reason no invoice could
   *  be stamped. Codes come from `@vysto/shared/fiscal`. */
  fiscalRegimen: string | null
  cfdiUse: string | null
  contactEmail: string | null
  timezone: string | null
  accent: string | null
  signature: string | null
}

export interface BusinessProfilePatch {
  legalName?: string
  rfc?: string
  fiscalPostalCode?: string
  fiscalCity?: string
  fiscalState?: string
  fiscalAddress?: string
  fiscalRegimen?: string
  cfdiUse?: string
  contactEmail?: string
  timezone?: string
  accent?: string
  signature?: string
}

/**
 * GET /admin/tenants/:id/business-profile — fiscal/contact/brand data off
 * tenants.config; the owner Settings page reads the same record back.
 */
export async function getBusinessProfile(tenantId: string): Promise<BusinessProfile> {
  const res = await apiClient.get<{ data: BusinessProfile }>(
    `/admin/tenants/${tenantId}/business-profile`,
  )
  return res.data.data
}

/**
 * PUT /admin/tenants/:id/business-profile — persists legal name, RFC and the
 * SAT fields (regimen, uso CFDI) that stamping a CFDI 4.0 requires.
 */
export async function saveBusinessProfile(
  tenantId: string,
  patch: BusinessProfilePatch,
): Promise<BusinessProfile> {
  const res = await apiClient.put<{ data: BusinessProfile }>(
    `/admin/tenants/${tenantId}/business-profile`,
    patch,
  )
  return res.data.data
}

export type MetaChannel = 'instagram' | 'messenger'

export interface ConnectedChannel {
  channel: MetaChannel
  label: string
  displayName: string
  externalId: string
}

/** All non-WhatsApp channels connected for a tenant (Instagram/Messenger). */
export async function listConnectedChannels(tenantId: string): Promise<ConnectedChannel[]> {
  const res = await apiClient.get<{ data: { channels: ConnectedChannel[] } }>(
    `/admin/tenants/${tenantId}/channels`,
  )
  return res.data.data.channels
}

export interface ConnectChannelInput {
  externalId: string
  displayName: string
  accessToken: string
}

/** Connect (or re-point) an Instagram/Messenger account for a tenant. */
export async function connectChannel(
  tenantId: string,
  channel: MetaChannel,
  input: ConnectChannelInput,
): Promise<{ connected: boolean; webhookSubscribed: boolean }> {
  const res = await apiClient.post<{ data: { connected: boolean; webhookSubscribed: boolean } }>(
    `/admin/tenants/${tenantId}/channels/${channel}`,
    input,
  )
  return res.data.data
}

/** Disconnect an Instagram/Messenger account. */
export async function disconnectChannel(tenantId: string, channel: MetaChannel): Promise<void> {
  await apiClient.delete(`/admin/tenants/${tenantId}/channels/${channel}`)
}

export interface WhatsappTemplate {
  purpose: 'appointment_reminder'
  /** The Meta-approved template name (e.g. 'appt_reminder'). */
  name: string
  /** The language code it was approved under (e.g. 'es_MX'). */
  language: string
}

/** The tenant's configured WhatsApp templates, one per purpose. */
export async function listWhatsappTemplates(tenantId: string): Promise<WhatsappTemplate[]> {
  const res = await apiClient.get<{ data: { templates: WhatsappTemplate[] } }>(
    `/admin/tenants/${tenantId}/whatsapp-templates`,
  )
  return res.data.data.templates
}

/** Record a client's Meta-approved template so the reminder can use it. */
export async function setWhatsappTemplate(
  tenantId: string,
  input: WhatsappTemplate,
): Promise<WhatsappTemplate> {
  const res = await apiClient.put<{ data: WhatsappTemplate }>(
    `/admin/tenants/${tenantId}/whatsapp-templates`,
    input,
  )
  return res.data.data
}

/** Remove a configured template. The reminder stops sending until one is set. */
export async function removeWhatsappTemplate(
  tenantId: string,
  purpose: WhatsappTemplate['purpose'],
): Promise<void> {
  await apiClient.delete(`/admin/tenants/${tenantId}/whatsapp-templates/${purpose}`)
}
